import { FC, useCallback, useMemo } from "react";
import { FreightBillingLineItemDto, LineItemBillingRatesDto, OrderLineItemDto, OrderReviewLineItemDto, RateToggle } from "../../../../dtos";
import { Edit, CheckCircleOutline } from "@mui/icons-material";
import { Dialog, DialogTitle, Typography, DialogContent, Grid, Button, DialogActions, FormControl, OutlinedInput, FormLabel } from "@mui/material";
import { formatCurrency, isOrderReviewLineItemDto, LineItemTypes } from "../../../../util";
import { RateInputForm } from "../../QuoteForm/LineItemForms";
import { IBillingRateFormProps, useBillingRateForm, BILLING_RATE_FORM_VALIDATED_FIELD } from "./useBillingRateForm";
import { FormInput, FormNumberInput } from "../../../CoreLib/library";
import { PricingMethodTypeSelect } from "../../../CommonInputs";
import { BilledToType, BilledToTypeToText } from "../../../../dtos/enums/BilledToType";
import { MaterialBilledToSelect } from "../../../CommonInputs/MaterialBilledToSelect";

export interface IBillingRateFormDialog extends IBillingRateFormProps {
    onClose: () => void;
    onConfirm: (freightBillingLineItem: FreightBillingLineItemDto) => void;
    open: boolean;
    billingRates: FreightBillingLineItemDto | LineItemBillingRatesDto;
    lineItem: OrderReviewLineItemDto | OrderLineItemDto;
    isFob: boolean;
    disabled: boolean;
    isOrder: boolean;
    customerName?: string;
}

export const BillingRateFormDialog: FC<IBillingRateFormDialog> = ({ onClose, onConfirm, open, lineItem, isFob, isOrder, disabled, billingRates, customerName }) => {
    const ignoreFields: BILLING_RATE_FORM_VALIDATED_FIELD[] = useMemo(() => {
        switch (lineItem.lineItem?.type) {
            case LineItemTypes.Material:
                if (isFob) {
                    return ['haulingRate', 'driverRate', 'brokerRate'];
                } else {
                    return [];
                }
            case LineItemTypes.Hourly:
                return ['haulingRate', 'driverRate', 'brokerRate'];
            default:
                return [];
        }
    }, [lineItem.lineItem?.type, isFob]);

    const {
        freightBillNumber,
        lineItemRate,
        haulingRate,
        flatHaulingRate,
        driverRate,
        flatDriverRate,
        brokerRate,
        flatBrokerRate,
        rateToggle,
        quoteRate,
        salesPrice,
        flatRateApplied,
        quantity,
        getCurrentFormValues,
        handleFormValueChange,
        fieldErrors,
        isValid,
        haulingTotalFlatRate,
        haulingTotalRate,
        isMaterialLineItemType,
        unitOfMeasure,
        pricingMethodType,
        isMaterialOnly,
        isBilledTo,
        billedTo,
    } = useBillingRateForm({
        billingRates,
        lineItem,
        isFob,
        isOrder,
        ignoreFields
    });

    const onCloseHandler = useCallback(() => {
        onClose();
    }, [onClose]);

    const onConfirmHandler = useCallback(() => {
        onConfirm(getCurrentFormValues());
        onClose();
    }, [getCurrentFormValues, onClose, onConfirm]);

    const lineItemDescription = useMemo(() => {
        if (isOrderReviewLineItemDto(lineItem)) {
            return lineItem.description;
        } else {
            return lineItem.lineItem?.description;
        }
    }, [lineItem]);

    const lineItemSiteName = useMemo(() => {
        if (isOrderReviewLineItemDto(lineItem)) {
            return (lineItem && 'site' in lineItem) ? lineItem?.site?.name : lineItem.lineItem?.site?.name;
        } else {
            return (lineItem && 'site' in lineItem) ? lineItem?.site?.name : lineItem.siteName;
        }
    }, [lineItem]);

    const lineItemZone = useMemo(() => {
        if (isOrderReviewLineItemDto(lineItem)) {
            return lineItem.zone;
        } else {
            return lineItem.lineItem?.zone;
        }
    }, [lineItem]);

    const getLineItemMaterialBillTo = useMemo(() => {
        // Use the passed customerName prop, or fall back to trying to get it from the lineItem
        const resolvedCustomerName = customerName ?? lineItem.customerName ?? (!(isOrderReviewLineItemDto(lineItem)) ? lineItem.order?.quote?.customer?.name : '') ?? '';

        return BilledToTypeToText(isBilledTo, resolvedCustomerName, billedTo);
    }, [billedTo, isBilledTo, lineItem, customerName]);

    const haulingFormInputs = useMemo(() => {
        return (
            <RateInputForm
                haulingRate={haulingRate}
                setHaulingRate={(value) => handleFormValueChange('haulingRate', value)}
                haulingRateFieldName='haulingRate'
                minimumFlatRate={flatHaulingRate}
                setMinimumFlatRate={(value) => handleFormValueChange('flatHaulingRate', value)}
                minimumFlatRateFieldName='flatHaulingRate'
                driverRate={driverRate}
                setDriverRate={(value) => handleFormValueChange('driverRate', value)}
                driverRateFieldName='driverRate'
                driverFlatRate={flatDriverRate}
                setDriverFlatRate={(value) => handleFormValueChange('flatDriverRate', value)}
                driverFlatRateFieldName='flatDriverRate'
                brokerRate={brokerRate}
                setBrokerRate={(value) => handleFormValueChange('brokerRate', value)}
                brokerRateFieldName='brokerRate'
                brokerFlatRate={flatBrokerRate}
                setBrokerFlatRate={(value) => handleFormValueChange('flatBrokerRate', value)}
                brokerFlatRateFieldName='flatBrokerRate'
                rateToggle={rateToggle ?? RateToggle.Standard}
                setRateToggle={(value) => handleFormValueChange('rateToggle', value)}
                fieldErrors={fieldErrors}
                disabled={disabled}
            />
        );
    }, [disabled, driverRate, fieldErrors, flatDriverRate, flatHaulingRate, handleFormValueChange, haulingRate, rateToggle, brokerRate, flatBrokerRate]);

    const materialLineItemForm = useMemo(() => {
        return (
            <>
                <Grid item container spacing={1}>
                    <Grid item container direction='column' xs={6}>
                        <Grid item>
                            <Typography variant='h3'>Material</Typography>
                        </Grid>
                        <Grid item>
                            <FormNumberInput
                                value={lineItemRate}
                                onChange={(e) => handleFormValueChange('lineItemRate', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                                label='Material Rate'
                                name='lineItemRate'
                                errorText={fieldErrors.get('lineItemRate')}
                                error={!!fieldErrors.get('lineItemRate')}
                                fullWidth
                                required
                                inputProps={{ min: 0 }}
                                isCurrency
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item>
                            <Grid item container direction='row' spacing={1}>
                                <Grid item xs={4}>
                                    <MaterialBilledToSelect
                                        selectedBilledTo={isBilledTo ?? BilledToType.TriAx}
                                        handleSelectedBilledToChange={(value) => handleFormValueChange('isBilledTo', value)}
                                        required
                                        errorMessage={fieldErrors.get('isBilledTo')}
                                    />
                                </Grid>
                                <Grid item xs={8}>
                                    <FormInput
                                        label='Material Bill To'
                                        value={getLineItemMaterialBillTo ?? ''}
                                        onChange={(e => handleFormValueChange('billedTo', e.target.value))}
                                        required={isBilledTo === BilledToType.Custom}
                                        errorText={fieldErrors.get('billedTo')}
                                        disabled={isBilledTo !== BilledToType.Custom}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {!isMaterialOnly && <Grid item container direction='column' spacing={2} xs={6}>
                        <Grid item>
                            <Typography variant='h3'>Hauling</Typography>
                        </Grid>
                        <Grid item>{haulingFormInputs}</Grid>
                    </Grid>}
                </Grid >
                <Grid item container spacing={1}>
                    <Grid item xs={6}>
                        <FormInput
                            value={formatCurrency(lineItemRate ?? 0)}
                            onChange={() => { }}
                            label='Material Total Rate'
                            name='totalRate'
                            fullWidth
                            disabled
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    {rateToggle !== RateToggle.Flat && (
                        <Grid item xs={rateToggle === RateToggle.Both ? 3 : 6}>
                            <FormControl fullWidth disabled>
                                <FormLabel>Hauling Total Rate</FormLabel>
                                <OutlinedInput
                                    value={formatCurrency(haulingTotalRate)}
                                    disabled
                                    inputProps={{ min: 0 }}
                                    endAdornment={!flatRateApplied ? <CheckCircleOutline color='error' /> : undefined}
                                />
                            </FormControl>
                        </Grid>
                    )}
                    {rateToggle !== RateToggle.Standard && (
                        <Grid item xs={rateToggle === RateToggle.Both ? 3 : 6}>
                            <FormControl fullWidth disabled>
                                <FormLabel>Hauling Total Rate (Flat)</FormLabel>
                                <OutlinedInput
                                    value={formatCurrency(haulingTotalFlatRate)}
                                    disabled
                                    inputProps={{ min: 0 }}
                                    endAdornment={flatRateApplied ? <CheckCircleOutline color='error' /> : undefined}
                                />
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </>
        );
    }, [lineItemRate, fieldErrors, disabled, isBilledTo, getLineItemMaterialBillTo, isMaterialOnly, haulingFormInputs, rateToggle, haulingTotalRate, flatRateApplied, haulingTotalFlatRate, handleFormValueChange]);

    const materialFOBLineItemForm = useMemo(() => {
        return (
            <Grid item container direction='column'>
                <Grid item>
                    <Typography variant='h3'>Material FOB</Typography>
                </Grid>
                <Grid item>
                    <FormNumberInput
                        value={lineItemRate}
                        onChange={(e) => handleFormValueChange('lineItemRate', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                        label='Material Rate'
                        name='lineItemRate'
                        errorText={fieldErrors.get('lineItemRate')}
                        error={!!fieldErrors.get('lineItemRate')}
                        fullWidth
                        required
                        inputProps={{ min: 0 }}
                        isCurrency
                        disabled={disabled}
                    />
                </Grid>
                <Grid item>
                    <FormNumberInput
                        value={lineItemRate ?? 0}
                        onChange={() => { }}
                        label='Material Total Rate'
                        name='totalRate'
                        fullWidth
                        disabled
                        inputProps={{ min: 0 }}
                        isCurrency
                    />
                </Grid>
            </Grid>
        );
    }, [disabled, fieldErrors, handleFormValueChange, lineItemRate]);

    const hourlyLineItemForm = useMemo(() => {
        return (
            <Grid item container direction='column' spacing={2}>
                <Grid item>
                    <Typography variant='h3'>Hourly</Typography>
                </Grid>
                <Grid item>
                    <FormNumberInput
                        value={lineItemRate}
                        onChange={(e) => handleFormValueChange('lineItemRate', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                        label='Hourly Rate'
                        name='lineItemRate'
                        errorText={fieldErrors.get('lineItemRate')}
                        error={!!fieldErrors.get('lineItemRate')}
                        fullWidth
                        required
                        inputProps={{ min: 0 }}
                        isCurrency
                        disabled={disabled}
                    />
                </Grid>
                <Grid item>
                    <FormNumberInput
                        value={driverRate}
                        onChange={(e) => handleFormValueChange('driverRate', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                        label='Driver Rate'
                        name='driverRate'
                        errorText={fieldErrors.get('driverRate')}
                        error={!!fieldErrors.get('driverRate')}
                        fullWidth
                        inputProps={{ min: 0 }}
                        isCurrency
                        disabled={disabled}
                    />
                </Grid>
                <Grid item>
                    <FormNumberInput
                        value={brokerRate}
                        onChange={(e) => handleFormValueChange('brokerRate', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                        label='Broker Rate'
                        name='brokerRate'
                        errorText={fieldErrors.get('brokerRate')}
                        error={!!fieldErrors.get('brokerRate')}
                        fullWidth
                        inputProps={{ min: 0 }}
                        isCurrency
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
        );
    }, [lineItemRate, fieldErrors, disabled, driverRate, brokerRate, handleFormValueChange]);

    const dumpLineItemForm = useMemo(() => {
        return (
            <Grid item container direction='column' spacing={2}>
                <Grid item>
                    <Typography variant='h3'>Dump</Typography>
                </Grid>
                <Grid item>
                    <FormNumberInput
                        value={lineItemRate}
                        onChange={(e) => handleFormValueChange('lineItemRate', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                        label='Dump Rate'
                        name='lineItemRate'
                        errorText={fieldErrors.get('lineItemRate')}
                        error={!!fieldErrors.get('lineItemRate')}
                        fullWidth
                        required
                        inputProps={{ min: 0 }}
                        isCurrency
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
        );
    }, [disabled, fieldErrors, handleFormValueChange, lineItemRate]);

    const miscLineItemForm = useMemo(() => {
        return (
            <Grid item container direction='column' spacing={2}>
                <Grid item>
                    <Typography variant='h3'>Misc.</Typography>
                </Grid>
                <Grid item>
                    <FormNumberInput
                        value={lineItemRate}
                        onChange={(e) => handleFormValueChange('lineItemRate', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                        label='Misc Rate'
                        name='lineItemRate'
                        errorText={fieldErrors.get('lineItemRate')}
                        error={!!fieldErrors.get('lineItemRate')}
                        fullWidth
                        required
                        inputProps={{ min: 0 }}
                        isCurrency
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
        );
    }, [disabled, fieldErrors, handleFormValueChange, lineItemRate]);

    const isStandardMaterialLineItem = useMemo(() => {
        return lineItem.lineItem?.type === LineItemTypes.Material && !isFob;
    }, [isFob, lineItem.lineItem?.type]);

    const isHourlyLineItem = useMemo(() => {
        return lineItem.lineItem?.type === LineItemTypes.Hourly;
    }, [lineItem.lineItem?.type]);

    const isMaterialOrDumpLineItem = useMemo(() => {
        return lineItem.lineItem?.type === LineItemTypes.Material || lineItem.lineItem?.type === LineItemTypes.Dump;
    }, [lineItem.lineItem?.type]);

    const form = useMemo(() => {
        switch (lineItem.lineItem?.type) {
            case LineItemTypes.Material:
                if (isFob) {
                    return materialFOBLineItemForm;
                } else {
                    return materialLineItemForm;
                }
            case LineItemTypes.Hourly:
                return hourlyLineItemForm;
            case LineItemTypes.Dump:
                return dumpLineItemForm;
            case LineItemTypes.Misc:
            default:
                return miscLineItemForm;
        }
    }, [dumpLineItemForm, hourlyLineItemForm, isFob, lineItem.lineItem?.type, materialFOBLineItemForm, materialLineItemForm, miscLineItemForm]);

    const fieldSizeIfOrder = useMemo(() => {
        return !isOrder ? 2 : 3;
    }, [isOrder]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg' PaperProps={{ sx: { width: '100%' } }}>
            <DialogTitle>
                <Typography variant='h2'>{<Edit />} Billing Rates</Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container direction='column' sx={{ marginTop: '10px' }}>
                    <Grid item container spacing={1}>
                        <Grid item container direction='column' xs={3}>
                            <FormNumberInput
                                label='Description'
                                name='description'
                                value={lineItemDescription ?? ''}
                                onChange={(event) => { }}
                                fullWidth
                                inputProps={{ type: 'text' }}
                                disabled
                            />
                        </Grid>
                        <Grid item container direction='column' xs={3}>
                            <FormNumberInput
                                label='Equipment Type'
                                name='equipmentType'
                                value={lineItem.equipmentType?.type + " - " + lineItem.equipmentType?.description}
                                onChange={(event) => { }}
                                fullWidth
                                inputProps={{ type: 'text' }}
                                disabled
                            />
                        </Grid>
                        <Grid item container direction='column' xs={3}>
                            <FormNumberInput
                                label='Site'
                                name='site'
                                value={lineItemSiteName ?? ''}
                                onChange={(event) => { }}
                                fullWidth
                                inputProps={{ type: 'text' }}
                                disabled
                            />
                        </Grid>
                        <Grid item container direction='column' xs={3}>
                            <FormNumberInput
                                label='Zone'
                                name='zone'
                                value={lineItemZone ?? ''}
                                onChange={(event) => { }}
                                fullWidth
                                inputProps={{ type: 'text' }}
                                disabled
                            />
                        </Grid>
                        {isHourlyLineItem && <Grid item container direction='row' spacing={1} xs={8}>
                            <Grid item xs={3}>
                                <MaterialBilledToSelect
                                    selectedBilledTo={isBilledTo}
                                    handleSelectedBilledToChange={(value) => handleFormValueChange('isBilledTo', value)}
                                    required
                                    errorMessage={fieldErrors.get('isBilledTo')}
                                    disabled={disabled}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormInput
                                    label='Material Bill To'
                                    value={getLineItemMaterialBillTo}
                                    onChange={(e => handleFormValueChange('billedTo', e.target.value))}
                                    required={isBilledTo === BilledToType.Custom}
                                    errorText={fieldErrors.get('billedTo')}
                                    disabled={isBilledTo !== BilledToType.Custom}
                                />
                            </Grid>
                        </Grid>}
                    </Grid>
                    <Grid item container spacing={2} pt={2}>
                        {form}
                    </Grid>
                    <Grid item container spacing={1} xs={12} wrap="nowrap" alignItems='center'>
                        {isStandardMaterialLineItem && <Grid item xs={2}>
                            <PricingMethodTypeSelect
                                selectedPricingMethodType={pricingMethodType}
                                onChange={(value) => handleFormValueChange('pricingMethodType', value)}
                                required
                                disabled={disabled}
                                errorMessage={fieldErrors.get('pricingMethodType')}
                            />
                        </Grid>}
                        {!isOrder && <Grid item xs={isStandardMaterialLineItem ? 2 : isMaterialOrDumpLineItem ? 3 : 4}>
                            <FormInput
                                label='Freight Bill #'
                                value={freightBillNumber}
                                onChange={(e) => handleFormValueChange('freightBillNumber', e.target.value)}
                                fullWidth
                                required
                                errorText={fieldErrors.get('freightBillNumber')}
                                disabled={disabled}
                            />
                        </Grid>}
                        <Grid item xs={isStandardMaterialLineItem ? fieldSizeIfOrder : isMaterialOrDumpLineItem ? 3 : 4}>
                            <FormNumberInput
                                label='Quantity'
                                name='quantity'
                                value={quantity}
                                onChange={(e) => handleFormValueChange('quantity', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                                fullWidth
                                required={isMaterialOrDumpLineItem}
                                inputProps={{ min: 0 }}
                                error={!!fieldErrors.get('quantity')}
                                errorText={fieldErrors.get('quantity')}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={isMaterialOrDumpLineItem ? fieldSizeIfOrder : 4}>
                            <FormInput
                                value={unitOfMeasure ?? ''}
                                onChange={() => { }}
                                label='Units'
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={isMaterialOrDumpLineItem ? fieldSizeIfOrder : 4}>
                            <FormInput
                                label={`Rate ${flatRateApplied && isMaterialLineItemType ? '(+ Flat)' : ''}`}
                                name='quoteRate'
                                value={formatCurrency(quoteRate)}
                                onChange={() => { }}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={isMaterialOrDumpLineItem ? fieldSizeIfOrder : 4}>
                            <FormInput
                                label='Total'
                                name='salesPrice'
                                value={formatCurrency(salesPrice)}
                                onChange={() => { }}
                                fullWidth
                                disabled
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
                <Grid container direction='row' justifyContent={'end'}>
                    <Grid item>
                        <Button variant='outlined' style={{ boxShadow: 'none', marginRight: '15px' }} onClick={onCloseHandler}>
                            {'Cancel'}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant='contained'
                            size='medium'
                            disabled={disabled}
                            onClick={() => {
                                if (isValid()) {
                                    onConfirmHandler();
                                } else {
                                    console.error('Form is invalid');
                                }
                            }}
                            sx={{ color: 'error', backgroundColor: 'primary' }}>
                            Confirm
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};
